import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import Pagination from "@/pages/category/components/Pagination";
import { categoryMeta } from "@/mocks/videos";
import { filterVideos } from "@/utils/videoUtils";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "movie" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const meta = categoryMeta[type];
  const typeKey = meta ? type : "";

  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const sort = searchParams.get("sort") || "hot";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const name = meta?.name ?? "全部影片";

  usePageMeta({
    title: `${name} - 西瓜视频免费`,
    description: `${name}频道：${meta?.intro ?? "西瓜视频免费为您整理高清影视内容，支持多条件筛选与快速查找。"}`,
    keywords: `西瓜视频免费,${name},免费${name}在线观看,高清视频`,
  });

  const filtered = useMemo(
    () => filterVideos({ type: typeKey || undefined, region, year, sort }),
    [typeKey, region, year, sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParam = (key: string, value: string, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "全部") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (resetPage) next.delete("page");
    setSearchParams(next);
    setMobileFilterOpen(false);
  };

  const handleTypeChange = (value: string) => {
    navigate(`/category/${value}`);
    setMobileFilterOpen(false);
  };

  const handleClear = () => {
    setSearchParams(new URLSearchParams());
    setMobileFilterOpen(false);
  };

  const handlePageChange = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: `${name}频道` }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          {name}
        </h1>
        {meta && (
          <p className="mt-2 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
            {meta.intro}
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <aside className="hidden lg:block w-full lg:w-64 shrink-0 rounded-lg border border-background-200 bg-background-100 p-5">
          <div className="mb-4 flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-600" />
            <span className="font-heading text-sm font-semibold text-foreground-950">筛选条件</span>
          </div>
          <FilterPanel
            type={typeKey || "movie"}
            region={region}
            year={year}
            sort={sort}
            onTypeChange={handleTypeChange}
            onRegionChange={(v) => updateParam("region", v)}
            onYearChange={(v) => updateParam("year", v)}
            onSortChange={(v) => updateParam("sort", v)}
            onClear={handleClear}
          />
        </aside>

        <div className="w-full lg:flex-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部内容
            </p>
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-medium text-foreground-800 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>

          {mobileFilterOpen && (
            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 lg:hidden">
              <FilterPanel
                type={typeKey || "movie"}
                region={region}
                year={year}
                sort={sort}
                onTypeChange={handleTypeChange}
                onRegionChange={(v) => updateParam("region", v)}
                onYearChange={(v) => updateParam("year", v)}
                onSortChange={(v) => updateParam("sort", v)}
                onClear={handleClear}
              />
            </div>
          )}

          {pageItems.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {pageItems.map((video, index) => (
                  <VideoCard key={video.id} video={video} index={index} />
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={handlePageChange} />
            </>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-emotion-sad-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                没有找到匹配的内容
              </h2>
              <p className="mt-1.5 max-w-sm text-sm text-foreground-600">
                当前筛选条件下暂无影片，试试调整地区、年份或其他条件。
              </p>
              <button
                type="button"
                onClick={handleClear}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                清除筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}